/**
 * @module utils/visuals/particle
 * @description Individual particle simulation.
 * Smooth motion with a restrained premium particle material.
 */

import type { ShapeTarget } from './types'
import { LIFECYCLE, seededRandom } from './config'
import { LifecycleState } from './lifecycle'
import { BRAND_COPPER_RGB } from './shape-design-spec'
import {
  getSurfaceTune,
  type CanvasSurface,
  type ParticleMass,
} from './surface-tune'

export type { ParticleConfig } from './types'
export type { ParticleMass }

export interface ParticleRenderStyle {
  time: number
  mode: 'listening' | 'thinking' | 'speaking' | 'idle'
  audioLevel: number
  bloom: boolean
  bloomIntensity: number
  isMorphing: boolean
  surface: CanvasSurface
  reducedMotion?: boolean
  weatherCondition?: 'sunny' | 'cloudy' | 'rainy' | 'snowy' | 'stormy' | 'windy' | 'fog' | undefined
}

/** Warm paper on black — must stay well above #000 / #050606. */
export const PARTICLE_INK_DARK: [number, number, number] = [242, 239, 234]
/** Editorial ink on #fafafa / #fff — DESIGN.md ink.primary. Never 226,224,220 on white. */
export const PARTICLE_INK_LIGHT: [number, number, number] = [17, 19, 21]

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value))
}

/** Canvas-signal palette — cool mist with sparse gold / cyan accents. */
function shapePalette(shape: string | undefined, isDarkMode: boolean): {
  base: [number, number, number]
  accentA: [number, number, number]
  accentB: [number, number, number]
} {
  if (!isDarkMode) {
    return {
      base: [18, 19, 22],
      accentA: shape === 'dna' || shape === 'workshop' ? [126, 87, 44] : [40, 70, 82],
      accentB: shape === 'consulting' || shape === 'constellation' ? [88, 76, 62] : [32, 35, 42],
    }
  }

  if (shape === 'dna' || shape === 'workshop') {
    return { base: [236, 238, 242], accentA: [252, 204, 128], accentB: [136, 214, 226] }
  }
  if (shape === 'brain' || shape === 'neural_network' || shape === 'constellation') {
    return { base: [232, 236, 244], accentA: [149, 211, 220], accentB: [236, 218, 165] }
  }
  if (shape === 'consulting' || shape === 'proposal' || shape === 'summary') {
    return { base: [238, 236, 230], accentA: [230, 201, 150], accentB: [155, 198, 204] }
  }

  return { base: [235, 237, 242], accentA: [226, 199, 145], accentB: [140, 202, 214] }
}

export function resolveInkColor(
  isDarkMode: boolean,
  mass: ParticleMass,
  shape: string | undefined,
  index: number,
): [number, number, number] {
  const base = isDarkMode ? PARTICLE_INK_DARK : PARTICLE_INK_LIGHT
  const copperEligible =
    mass === 'anchor' || (shape === 'dna' && mass !== 'dust' && index % 6 === 0)
  if (!copperEligible) return base
  const mix = mass === 'anchor' ? 0.52 : 0.4
  return [
    Math.round(base[0] + (BRAND_COPPER_RGB[0] - base[0]) * mix),
    Math.round(base[1] + (BRAND_COPPER_RGB[1] - base[1]) * mix),
    Math.round(base[2] + (BRAND_COPPER_RGB[2] - base[2]) * mix),
  ]
}

export function resolveParticleMass(index: number, surface?: CanvasSurface): ParticleMass {
  const tune = getSurfaceTune(surface)
  const u = seededRandom(index * 997 + 3)
  if (u > 1 - tune.anchorShare) return 'anchor'
  if (u > 1 - tune.anchorShare - tune.midShare) return 'mid'
  return 'dust'
}

/**
 * Represents a single particle in the visual system.
 * V9 simplified: physics, alpha, direct draw.
 */
export class Particle {
  x = 0
  y = 0
  vx = 0
  vy = 0
  hue = 0
  private radius = 0
  index = 0
  resolvedRadius = 0
  depthScale = 1
  mass: ParticleMass = 'dust'
  /** Flow-field heading, lerped — ticks renderer orients on this. */
  angle = 0
  /** Position history for the ribbons renderer. Allocated on first use. */
  trailX: Float32Array | null = null
  trailY: Float32Array | null = null
  readonly config: {
    index: number
    baseRadius: number
    baseAlpha: number
    maxLife?: number
    birthFrames?: number
    deathFrames?: number
    hue?: number
  }
  lifecycle: LifecycleState
  private _resolvedAlpha = 0

  constructor(w: number, h: number, config: {
    index: number
    baseRadius: number
    baseAlpha: number
    maxLife?: number
    birthFrames?: number
    deathFrames?: number
    hue?: number
    spawnRadius?: number
    mass?: ParticleMass
  }) {
    this.config = config
    this.index = config.index
    this.mass = config.mass ?? resolveParticleMass(config.index)
    const spawnRadius = config.spawnRadius ?? LIFECYCLE.spawnRadius
    const angle = seededRandom(config.index) * Math.PI * 2
    const dist = seededRandom(config.index + 1) * spawnRadius
    this.x = Math.round(w / 2 + Math.cos(angle) * dist)
    this.y = Math.round(h / 2 + Math.sin(angle) * dist)
    this.radius = config.baseRadius
    this.baseAlpha = config.baseAlpha
    this.hue = config.hue ?? Math.round(seededRandom(config.index + 2) * 360)
    const maxLife =
      config.maxLife ?? Math.round(LIFECYCLE.maxLifeBase + seededRandom(config.index + 3) * LIFECYCLE.maxLifeRange)
    this.lifecycle = new LifecycleState(
      Math.max(0, maxLife),
      Math.max(0, config.birthFrames ?? LIFECYCLE.birthFrames),
      Math.max(0, config.deathFrames ?? LIFECYCLE.deathFrames)
    )
    this.resolvedRadius = this.radius
  }

  get rad(): number { return this.resolvedRadius }

  /** Record one frame of position history (newest at index 0). */
  pushTrail(cap: number): void {
    let tx = this.trailX
    let ty = this.trailY
    if (!tx || !ty || tx.length !== cap) {
      tx = new Float32Array(cap).fill(this.x)
      ty = new Float32Array(cap).fill(this.y)
      this.trailX = tx
      this.trailY = ty
      return
    }
    for (let i = cap - 1; i > 0; i--) {
      tx[i] = tx[i - 1]!
      ty[i] = ty[i - 1]!
    }
    tx[0] = this.x
    ty[0] = this.y
  }

  getSpeed(): number {
    return Math.sqrt(this.vx * this.vx + this.vy * this.vy)
  }

  baseAlpha: number

  get targetAlpha(): number {
    return this.baseAlpha * this.lifecycle.computeAlphaScale()
  }

  get resolvedAlpha(): number { return this._resolvedAlpha }

  /** Runtime update used by the Antigravity canvas. */
  updateV9(
    target: ShapeTarget,
    forces: { fx: number; fy: number },
    _time: number,
    reducedMotion = false,
  ): void {
    // Spring toward shape target
    const ax = (target.x - this.x) * target.spring
    const ay = (target.y - this.y) * target.spring

    this.vx += ax + forces.fx
    this.vy += ay + forces.fy

    // Friction
    this.vx *= target.friction
    this.vy *= target.friction

    if (!reducedMotion && target.noise > 0) {
      const phase = _time * 0.0012 + this.index * 0.173
      const seedPhase = seededRandom(this.index * 811 + 17) * Math.PI * 2
      this.vx += Math.cos(phase + seedPhase) * target.noise * 0.55
      this.vy += Math.sin(phase * 1.17 + seedPhase) * target.noise * 0.55
    }

    // Integrate
    this.x += this.vx
    this.y += this.vy

    // Twinkle in place — never teleport on expiry (DESIGN.md lifecycle)
    if (this.lifecycle.isExpired) this.lifecycle.reset()
    this.lifecycle.update()

    // Resolve radius with depth scale
    const ds = target.depthScale ?? 1
    this.depthScale += (ds - this.depthScale) * 0.15
    const targetR = this.radius * this.depthScale
    this.resolvedRadius += (targetR - this.resolvedRadius) * 0.2

    // Alpha
    this._resolvedAlpha = clamp01(this.targetAlpha * (target.alphaHint ?? 1))
  }

  /**
   * Organic particle draw — circular, speed-reactive, monochrome.
   * Dark mode: white/grey glowing dots.
   * Light mode: dark charcoal dots.
   */
  drawSimple(
    ctx: CanvasRenderingContext2D,
    isDarkMode: boolean,
    shape?: string,
    style?: ParticleRenderStyle
  ): void {
    if (this.resolvedRadius < 0.05 || this._resolvedAlpha < 0.05) return

    const speed = this.getSpeed()
    const time = style?.time ?? 0
    const audio = style?.audioLevel ?? 0
    const reduced = style?.reducedMotion === true
    const depth = Math.max(0.35, Math.min(2.4, this.depthScale))
    const shimmer = reduced ? 1 : 1 + Math.sin(time * 0.004 + this.index * 0.29) * 0.035
    const speedPulse = reduced ? 1 : 1 + Math.min(speed * 0.045, 0.32)
    const audioPulse = reduced ? 1 : 1 + audio * 0.18
    const r = Math.max(0.12, this.resolvedRadius * speedPulse * audioPulse * shimmer)

    const modeAlpha =
      style?.mode === 'thinking' ? 0.92 :
      style?.mode === 'speaking' ? 1.04 :
      style?.mode === 'listening' ? 0.96 : 0.88
    const surfaceAlpha = style?.surface === 'chat' ? 0.72 : 1
    const alpha = clamp01(this._resolvedAlpha * modeAlpha * surfaceAlpha * (0.62 + depth * 0.18))

    const palette = shapePalette(shape, isDarkMode)
    const accentSeed = seededRandom(this.index * 1543 + 71)
    const accentBand = accentSeed > 0.972 || (style?.isMorphing && this.index % 17 === 0)
    const accentMix = accentSeed > 0.986 ? palette.accentA : palette.accentB
    const base = accentBand ? accentMix : palette.base
    const warmth = accentBand ? 1 : seededRandom(this.index * 617 + 9) * 0.12
    const colR = Math.round(base[0] + (palette.accentA[0] - base[0]) * warmth)
    const colG = Math.round(base[1] + (palette.accentA[1] - base[1]) * warmth)
    const colB = Math.round(base[2] + (palette.accentA[2] - base[2]) * warmth)

    if (!reduced && speed > 1.25 && r > 0.34) {
      const trailAlpha = clamp01(alpha * Math.min(0.18, speed * 0.035))
      ctx.beginPath()
      ctx.moveTo(this.x - this.vx * 1.9, this.y - this.vy * 1.9)
      ctx.lineTo(this.x, this.y)
      ctx.strokeStyle = `rgba(${colR},${colG},${colB},${trailAlpha.toFixed(3)})`
      ctx.lineWidth = Math.max(0.35, r * 0.55)
      ctx.stroke()
    }

    const bloom = style?.bloom ?? false
    const haloEligible =
      !reduced && bloom && (accentBand || depth > 1.2 || style?.mode === 'speaking') && this.index % 3 !== 1
    if (haloEligible) {
      const bloomIntensity = Math.max(0.35, style?.bloomIntensity ?? 0.8)
      const haloAlpha = clamp01(alpha * 0.12 * bloomIntensity * (style?.surface === 'chat' ? 0.74 : 1))
      ctx.beginPath()
      ctx.arc(this.x, this.y, Math.max(r * 2.65, 1.2), 0, Math.PI * 2)
      ctx.fillStyle = `rgba(${colR},${colG},${colB},${haloAlpha.toFixed(3)})`
      ctx.fill()
    }

    if (!reduced && r > 0.55 && (depth > 1.15 || accentBand)) {
      ctx.beginPath()
      ctx.arc(this.x, this.y, r * 1.55, 0, Math.PI * 2)
      ctx.fillStyle = `rgba(${colR},${colG},${colB},${(alpha * 0.12).toFixed(3)})`
      ctx.fill()
    }

    // Weather-specific particle forms
    const weather = style?.weatherCondition
    if (weather === 'rainy' || weather === 'stormy') {
      const streakLen = weather === 'stormy' ? r * 3.2 : r * 2.2
      const streakAlpha = alpha * (weather === 'stormy' ? 0.55 : 0.42)
      ctx.beginPath()
      ctx.moveTo(this.x, this.y - streakLen * 0.3)
      ctx.lineTo(this.x, this.y + streakLen)
      ctx.strokeStyle = `rgba(${colR},${colG},${colB},${streakAlpha.toFixed(3)})`
      ctx.lineWidth = Math.max(0.35, r * 0.45)
      ctx.lineCap = 'round'
      ctx.stroke()
    } else if (weather === 'snowy') {
      const snowR = r * 1.35
      const snowAlpha = alpha * 0.62
      ctx.beginPath()
      ctx.arc(this.x, this.y, snowR, 0, Math.PI * 2)
      ctx.fillStyle = `rgba(${colR},${colG},${colB},${snowAlpha.toFixed(3)})`
      ctx.fill()
      // soft halo for snow
      ctx.beginPath()
      ctx.arc(this.x, this.y, snowR * 2.2, 0, Math.PI * 2)
      ctx.fillStyle = `rgba(${colR},${colG},${colB},${(snowAlpha * 0.08).toFixed(3)})`
      ctx.fill()
    } else if (weather === 'fog') {
      const fogR = r * 2.8
      const fogAlpha = alpha * 0.22
      ctx.beginPath()
      ctx.arc(this.x, this.y, fogR, 0, Math.PI * 2)
      ctx.fillStyle = `rgba(${colR},${colG},${colB},${fogAlpha.toFixed(3)})`
      ctx.fill()
    } else if (weather === 'windy') {
      const windLen = r * 2.6
      const windAlpha = alpha * 0.48
      ctx.beginPath()
      ctx.moveTo(this.x - windLen * 0.5, this.y)
      ctx.lineTo(this.x + windLen, this.y)
      ctx.strokeStyle = `rgba(${colR},${colG},${colB},${windAlpha.toFixed(3)})`
      ctx.lineWidth = Math.max(0.28, r * 0.38)
      ctx.lineCap = 'round'
      ctx.stroke()
    } else {
      ctx.beginPath()
      ctx.arc(this.x, this.y, r, 0, Math.PI * 2)
      ctx.fillStyle = `rgba(${colR},${colG},${colB},${alpha.toFixed(3)})`
      ctx.fill()
    }

    if (accentBand && r > 0.42) {
      ctx.beginPath()
      ctx.arc(this.x, this.y, Math.max(0.26, r * 0.36), 0, Math.PI * 2)
      ctx.fillStyle = isDarkMode
        ? `rgba(255,255,255,${(alpha * 0.72).toFixed(3)})`
        : `rgba(255,255,255,${(alpha * 0.34).toFixed(3)})`
      ctx.fill()
    }
  }

  // ── Deprecated methods — kept for backward compat, not used in V9 pipeline ──

  update(fp: import('./types').FrameParams): void {
    const ax = (fp.targetX - this.x) * fp.spring
    const ay = (fp.targetY - this.y) * fp.spring
    this.vx += ax + fp.mouseForceX + fp.antigravityX
    this.vy += ay + fp.mouseForceY + fp.antigravityY
    this.vx *= fp.friction
    this.vy *= fp.friction
    if (fp.noiseAmplitude > 0) {
      const n = seededRandom(this.lifecycle.age + this.index * 1000)
      this.vx += (n - 0.5) * fp.noiseAmplitude
      this.vy += (seededRandom(this.lifecycle.age + this.index * 1001) - 0.5) * fp.noiseAmplitude
    }
    if (fp.orbitBoost > 0) {
      const dx = fp.targetX - this.x, dy = fp.targetY - this.y
      const dist = Math.sqrt(dx * dx + dy * dy) + 0.001
      const orbital = fp.orbitBoost * 0.01
      this.vx += -dy / dist * orbital
      this.vy +=  dx / dist * orbital
    }
    this.vx += Math.cos(fp.driftAngle) * fp.driftMagnitude
    this.vy += Math.sin(fp.driftAngle) * fp.driftMagnitude
    this.x = Math.round(this.x + this.vx)
    this.y = Math.round(this.y + this.vy)
    this.lifecycle.update()
    const targetR = Math.round(this.radius * (1 + fp.audioSizeBoost))
    const ds = fp.depthScale ?? 1
    this.depthScale += (ds - this.depthScale) * LIFECYCLE.scaleSmoothing
    this.resolvedRadius = Math.round(
      this.resolvedRadius +
        (targetR * this.depthScale - this.resolvedRadius) * LIFECYCLE.radSmoothing
    )
    if (this.lifecycle.isExpired && this.lifecycle.life > 0) {
      this.lifecycle.reset()
      const respawnSeed = this.index * 10000 + this.lifecycle.life
      this.x = Math.round(seededRandom(respawnSeed) * fp.canvasW)
      this.y = Math.round(seededRandom(respawnSeed + 1) * fp.canvasH)
    }
    this._resolvedAlpha = this.targetAlpha
  }

  draw(
    ctx: CanvasRenderingContext2D,
    _hueFn: (t: number, hueShift: number) => { r: number; g: number; b: number },
    materialType: import('./types').ParticleMaterial,
    _glow: { radius: number; alpha: number },
    overrideDepthScale?: number,
    isDarkMode = false,
    shape?: string
  ): void {
    const ds = overrideDepthScale ?? this.depthScale
    const r = Math.max(1, this.resolvedRadius * 2 * ds)
    const px = this.x
    const py = this.y
    if (r < 1 || this.resolvedAlpha < 0.05) return

    const baseAlpha = this.resolvedAlpha.toFixed(3)
    let colR: number, colG: number, colB: number
    if (isDarkMode) {
      colR = 255; colG = 255; colB = 255
      if (shape === 'neural_network' || shape === 'brain' || shape === 'code') {
        colR = 240; colG = 245; colB = 255
      } else if (shape === 'dna' || shape === 'grid' || shape === 'chart') {
        colR = 255; colG = 250; colB = 245
      }
    } else {
      colR = 20; colG = 20; colB = 20
      if (shape === 'neural_network' || shape === 'brain' || shape === 'code') {
        colR = 25; colG = 22; colB = 30
      } else if (shape === 'dna' || shape === 'grid' || shape === 'chart') {
        colR = 30; colG = 25; colB = 22
      }
    }

    ctx.save()
    switch (materialType) {
      case 'wire': {
        ctx.strokeStyle = `rgba(${colR},${colG},${colB},${baseAlpha})`
        ctx.lineWidth = Math.max(1, r * 0.25)
        ctx.strokeRect(px - r * 0.5, py - r * 0.5, r, r)
        break
      }
      case 'sharp':
      case 'glowing':
      case 'trailing':
      case 'soft':
      default: {
        if (isDarkMode) ctx.globalCompositeOperation = 'lighter'
        ctx.fillStyle = `rgba(${colR},${colG},${colB},${baseAlpha})`
        ctx.fillRect(px - r * 0.5, py - r * 0.5, r, r)
        break
      }
    }
    ctx.restore()
  }
}

/**
 * Build a deterministic particle config for a given particle index.
 * V9 style: baseRadius 0.3-1.0, baseAlpha 0.4-1.0
 */
export function buildParticleConfig(
  index: number,
  total: number,
  _tier: { count: number },
  options?: { spawnRadius?: number; coreScale?: number; surface?: CanvasSurface },
): {
  index: number
  totalParticles: number
  baseRadius: number
  baseAlpha: number
  spawnRadius?: number
  mass: ParticleMass
} {
  const sr = (s: number) => seededRandom(index * 997 + s)
  const mass = resolveParticleMass(index, options?.surface)
  const coreScale = options?.coreScale ?? 1
  const radius =
    mass === 'anchor'
      ? (0.7 + sr(1) * 0.5) * coreScale
      : mass === 'mid'
        ? (0.32 + sr(1) * 0.28) * coreScale
        : (0.12 + sr(1) * 0.28) * coreScale
  const alpha =
    mass === 'anchor' ? 0.72 + sr(2) * 0.28 : mass === 'mid' ? 0.52 + sr(2) * 0.32 : 0.4 + sr(2) * 0.45
  const cfg: {
    index: number
    totalParticles: number
    baseRadius: number
    baseAlpha: number
    spawnRadius?: number
    mass: ParticleMass
  } = {
    index,
    totalParticles: total,
    baseRadius: radius,
    baseAlpha: alpha,
    mass,
  }
  if (options?.spawnRadius !== undefined) cfg.spawnRadius = options.spawnRadius
  return cfg
}
